/**
 * FormulaText Component
 *
 * Renders mathematical formulas in react-pdf with proper subscript/superscript support.
 * Since @react-pdf/renderer doesn't support unicode subscripts well with standard fonts,
 * this component uses positioning to render subscripts and superscripts.
 *
 * Uses Manrope font for proper Greek letter support (e.g., λ, α, β).
 */

import { Font, StyleSheet, Text, View } from '@react-pdf/renderer'
import ManropeBold from 'assets/Manrope-Bold.ttf'
import ManropeMedium from 'assets/Manrope-Medium.ttf'
import ManropeRegular from 'assets/Manrope-Regular.ttf'
import React from 'react'

// ==================== Font Registration ====================

// Register Manrope font family for Greek letter support
Font.register({
  family: 'Manrope',
  fonts: [
    { src: ManropeRegular },
    { src: ManropeMedium, fontWeight: 'medium' },
    { src: ManropeBold, fontWeight: 'bold' },
  ],
})

// ==================== Types ====================

export type FormulaPart =
  | { type: 'text'; value: string }
  | { type: 'sub'; value: string }
  | { type: 'sup'; value: string }
  | { type: 'frac'; num: FormulaPart[]; den: FormulaPart[] }

export interface FormulaDefinition {
  /** Plain text label for display (e.g., "t_mm" becomes "tₘₘ") */
  label: string
  /** Structured parts for PDF rendering */
  parts: FormulaPart[]
}

export interface FormulaTextProps {
  parts: FormulaPart[]
  baseFontSize?: number
  color?: string
}

// ==================== Styles ====================

const createStyles = (baseFontSize: number, color: string) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'baseline',
      flexWrap: 'wrap',
    },
    baseText: {
      fontFamily: 'Manrope',
      fontSize: baseFontSize,
      color,
    },
    subscript: {
      fontFamily: 'Manrope',
      fontSize: baseFontSize * 0.65,
      color,
      marginBottom: -baseFontSize * 0.15,
    },
    superscript: {
      fontFamily: 'Manrope',
      fontSize: baseFontSize * 0.65,
      color,
      marginTop: -baseFontSize * 0.35,
    },
    fracContainer: {
      flexDirection: 'column',
      alignItems: 'center',
      marginHorizontal: 2,
    },
    fracNum: {
      flexDirection: 'row',
      borderBottomWidth: 0.5,
      borderBottomColor: color,
      paddingBottom: 1,
    },
    fracDen: {
      flexDirection: 'row',
      paddingTop: 1,
    },
  })

// ==================== Component ====================

/**
 * Renders a formula part recursively
 */
const renderPart = (
  part: FormulaPart,
  index: number,
  styles: ReturnType<typeof createStyles>,
  baseFontSize: number,
  color: string
): React.ReactNode => {
  switch (part.type) {
    case 'text':
      return (
        <Text key={index} style={styles.baseText}>
          {part.value}
        </Text>
      )
    case 'sub': {
      const subLength = part.value ? part.value.length : 1
      // Slight tuck for single-letter subscripts; none for multi-letter to avoid bunching
      const marginLeft = subLength <= 1 ? -baseFontSize * 0.08 : 0
      return (
        <Text key={index} style={[styles.subscript, { marginLeft }]}>
          {part.value}
        </Text>
      )
    }
    case 'sup': {
      const supLength = part.value ? part.value.length : 1
      const marginLeft = supLength <= 1 ? -baseFontSize * 0.08 : 0
      return (
        <Text key={index} style={[styles.superscript, { marginLeft }]}>
          {part.value}
        </Text>
      )
    }
    case 'frac':
      return (
        <View key={index} style={styles.fracContainer}>
          <View style={styles.fracNum}>
            {part.num.map((p, i) =>
              renderPart(p, i, styles, baseFontSize * 0.8, color)
            )}
          </View>
          <View style={styles.fracDen}>
            {part.den.map((p, i) =>
              renderPart(p, i, styles, baseFontSize * 0.8, color)
            )}
          </View>
        </View>
      )
    default:
      return null
  }
}

/**
 * FormulaText - Renders mathematical formulas with proper subscript/superscript
 */
export const FormulaText: React.FC<FormulaTextProps> = ({
  parts,
  baseFontSize = 9,
  color = '#000',
}) => {
  const styles = createStyles(baseFontSize, color)

  return (
    <View style={styles.container}>
      {parts.map((part, index) =>
        renderPart(part, index, styles, baseFontSize, color)
      )}
    </View>
  )
}

// ==================== Helper Functions ====================

/**
 * Greek letter and special symbol mappings
 */
const SYMBOL_MAP: Record<string, string> = {
  // Greek letters
  lambda: 'λ',
  Lambda: 'Λ',
  alpha: 'α',
  Alpha: 'Α',
  beta: 'β',
  Beta: 'Β',
  gamma: 'γ',
  Gamma: 'Γ',
  delta: 'δ',
  Delta: 'Δ',
  epsilon: 'ε',
  Epsilon: 'Ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  Theta: 'Θ',
  iota: 'ι',
  kappa: 'κ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  Xi: 'Ξ',
  pi: 'π',
  Pi: 'Π',
  rho: 'ρ',
  sigma: 'σ',
  Sigma: 'Σ',
  tau: 'τ',
  upsilon: 'υ',
  phi: 'φ',
  Phi: 'Φ',
  chi: 'χ',
  psi: 'ψ',
  Psi: 'Ψ',
  omega: 'ω',
  Omega: 'Ω',
  // Math symbols
  sqrt: '√',
  inf: '∞',
  pm: '±',
  times: '×',
  div: '÷',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  approx: '≈',
}

/**
 * Replace Greek letter names and special symbols with their Unicode equivalents
 */
function replaceSymbols(text: string): string {
  let result = text
  // Sort by length descending to match longer names first (e.g., "lambda" before "a")
  const sortedKeys = Object.keys(SYMBOL_MAP).sort((a, b) => b.length - a.length)
  for (const name of sortedKeys) {
    // Use word boundaries to avoid partial replacements
    const regex = new RegExp(`\\b${name}\\b`, 'g')
    result = result.replace(regex, SYMBOL_MAP[name])
  }
  return result
}

/**
 * Parse a simple formula string into parts
 * Supports: _x for subscript, ^x for superscript, {group} for grouping
 *
 * Examples:
 * - "t_mm" -> t with subscript mm
 * - "t^c" -> t with superscript c
 * - "t_{ml}" -> t with subscript ml
 * - "MAWP^L" -> MAWP with superscript L
 * - "lambda" -> λ (Greek letter)
 * - "sqrt(x)" -> √(x)
 */
export function parseFormula(formula: string): FormulaPart[] {
  const parts: FormulaPart[] = []
  let i = 0

  while (i < formula.length) {
    const char = formula[i]

    if (char === '_' || char === '^') {
      const type = char === '_' ? 'sub' : 'sup'
      i++

      // Check for grouped content {xxx}
      if (formula[i] === '{') {
        const endBrace = formula.indexOf('}', i)
        if (endBrace !== -1) {
          parts.push({
            type,
            value: replaceSymbols(formula.slice(i + 1, endBrace)),
          })
          i = endBrace + 1
          continue
        }
      }

      // Single character subscript/superscript
      if (i < formula.length) {
        parts.push({ type, value: formula[i] })
        i++
      }
    } else {
      // Collect regular text until we hit a special character
      let text = ''
      while (i < formula.length && formula[i] !== '_' && formula[i] !== '^') {
        text += formula[i]
        i++
      }
      if (text) {
        // Replace Greek letters and symbols in the text
        parts.push({ type: 'text', value: replaceSymbols(text) })
      }
    }
  }

  return parts
}

/**
 * Create formula parts for common patterns
 */
export const formulaBuilders = {
  /** Variable with subscript: x_sub */
  withSub: (base: string, sub: string): FormulaPart[] => [
    { type: 'text', value: base },
    { type: 'sub', value: sub },
  ],

  /** Variable with superscript: x^sup */
  withSup: (base: string, sup: string): FormulaPart[] => [
    { type: 'text', value: base },
    { type: 'sup', value: sup },
  ],

  /** Variable with both: x_sub^sup */
  withSubSup: (base: string, sub: string, sup: string): FormulaPart[] => [
    { type: 'text', value: base },
    { type: 'sub', value: sub },
    { type: 'sup', value: sup },
  ],

  /** Plain text */
  text: (value: string): FormulaPart[] => [{ type: 'text', value }],

  /** Fraction */
  frac: (num: FormulaPart[], den: FormulaPart[]): FormulaPart[] => [
    { type: 'frac', num, den },
  ],
}
